import { listingEventUnixFromPublicData } from './listingEventDate';

/** URL `sort` value for soonest event first (not sent verbatim to Marketplace API). */
export const EVENT_DATE_SORT_KEY = 'eventDate';

/** Marketplace API sort: soonest event first (ascending unix). `-` would be farthest-future first. */
export const MARKETPLACE_SORT_EVENT_DATE_ASC = 'pub_eventDateUnix';

/**
 * Keyword or location search (not the unfiltered home browse).
 * Those queries default to soonest event date unless the user picked a sort.
 */
export const searchHasQueryIntent = (searchParams = {}) => {
  const keywords = searchParams.keywords != null ? String(searchParams.keywords).trim() : '';
  const address = searchParams.address != null ? String(searchParams.address).trim() : '';
  return Boolean(keywords || address);
};

/**
 * Effective sort for UI + client-side ordering when `sort` is absent from the URL.
 * Home/browse (no keywords or address) uses `defaultSortKey` (newest listed).
 * A text or location search uses soonest event date — matching other ticketing sites.
 */
export const effectiveSearchSortParam = (sortParam, sortConfig = {}, searchParams = {}) => {
  const {
    relevanceKey = 'relevance',
    defaultSortKey = EVENT_DATE_SORT_KEY,
    eventDateSortKey = EVENT_DATE_SORT_KEY,
  } = sortConfig;
  if (sortParam === relevanceKey) {
    return sortParam;
  }
  if (sortParam == null || sortParam === '') {
    return searchHasQueryIntent(searchParams) ? eventDateSortKey : defaultSortKey;
  }
  return sortParam;
};

/**
 * Map app sort keys to Marketplace API `sort` (or omit when API should use its own default).
 */
export const mapSortParamForMarketplaceApi = (sortParam, sortConfig = {}, searchParams = {}) => {
  const effective = effectiveSearchSortParam(sortParam, sortConfig, searchParams);
  const { relevanceKey = 'relevance', eventDateSortKey = EVENT_DATE_SORT_KEY } = sortConfig;

  if (!effective || effective === relevanceKey) {
    return null;
  }
  if (effective === eventDateSortKey) {
    return MARKETPLACE_SORT_EVENT_DATE_ASC;
  }
  return effective;
};

/**
 * Unix seconds for ordering a Sharetribe listing by event date (soonest = smallest).
 * Listings without a usable event date sort last.
 */
export const listingEventUnixForSort = listing => {
  const pd = listing?.attributes?.publicData;
  if (!pd || typeof pd !== 'object') {
    return Number.POSITIVE_INFINITY;
  }

  const fromEvent = listingEventUnixFromPublicData(pd);
  if (fromEvent != null && Number.isFinite(fromEvent)) {
    return fromEvent;
  }

  const createdAt = listing?.attributes?.createdAt;
  if (createdAt != null) {
    const t = Date.parse(String(createdAt));
    if (Number.isFinite(t)) {
      return Math.floor(t / 1000);
    }
  }

  return Number.POSITIVE_INFINITY;
};

/**
 * Client-side listing order for the current result page (matches primary-event sort keys).
 */
export const sortListingsForSearch = (listings, sortParam, sortConfig = {}, searchParams = {}) => {
  const arr = Array.isArray(listings) ? [...listings] : [];
  const effective = effectiveSearchSortParam(sortParam, sortConfig, searchParams);
  const { relevanceKey = 'relevance', eventDateSortKey = EVENT_DATE_SORT_KEY } = sortConfig;

  if (!effective || effective === relevanceKey) {
    return arr;
  }

  if (effective === eventDateSortKey) {
    arr.sort((a, b) => listingEventUnixForSort(a) - listingEventUnixForSort(b));
    return arr;
  }

  if (effective === '-price') {
    const priceOf = l => {
      const p = l?.attributes?.price;
      const amount = p?.amount;
      return Number.isFinite(amount) ? amount : 0;
    };
    arr.sort((a, b) => priceOf(a) - priceOf(b));
    return arr;
  }
  if (effective === 'price') {
    const priceOf = l => {
      const p = l?.attributes?.price;
      const amount = p?.amount;
      return Number.isFinite(amount) ? amount : 0;
    };
    arr.sort((a, b) => priceOf(b) - priceOf(a));
    return arr;
  }

  const createdAtSec = l => {
    const raw = l?.attributes?.createdAt;
    if (raw == null) {
      return 0;
    }
    const t = Date.parse(String(raw));
    return Number.isFinite(t) ? t : 0;
  };

  if (effective === 'createdAt') {
    arr.sort((a, b) => createdAtSec(b) - createdAtSec(a));
    return arr;
  }
  if (effective === '-createdAt') {
    arr.sort((a, b) => createdAtSec(a) - createdAtSec(b));
    return arr;
  }

  return arr;
};
